import { Injectable } from "@angular/core";
import { Order, CityData, VenueData } from "../models/order";
import {
  ByProductType,
  ByDayOfWeek,
  ByActiveDays,
  Totals,
  BarChartData,
  ByCity,
  ByVenue
} from "../models/analytics";
import { ProductType } from "../product/enums/product-type.enum";
import { DateService } from "./date.service";
import * as moment from "moment";
import { DocumentReference, AngularFirestore } from "@angular/fire/firestore";
import { AnalyticsFilterService } from "./filter.service";
import { Timeframe, Venue } from "../models";
import { Subscription, Observable } from "rxjs";
import * as XLSX from "xlsx";
import * as FileSaver from "file-saver";
import { OrderApi } from "../api/order.api";

@Injectable({
  providedIn: "root"
})
export class AnalyticsService {
  public EXCEL_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  public EXCEL_EXTENSION = ".xlsx";
  public exportAsExcelFile(): void {
    const totals: XLSX.WorkSheet = XLSX.utils.json_to_sheet([
      this.periodTotals
    ]);
    const byProduct: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
      this.byProductType
    );
    const byActiveDays: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
      this.byActiveDays
    );
    const byDayOfWeek: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
      this.byDayOfWeek
    );
    const byCity: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.byCity);
    const byVenue: XLSX.WorkSheet = XLSX.utils.json_to_sheet(this.byVenue);
    const orders: XLSX.WorkSheet = XLSX.utils.json_to_sheet(
      this.filtered.map(order => this.filter.toCSV(order))
    );
    const workbook: XLSX.WorkBook = {
      Sheets: {
        "Period Totals": totals,
        "By Product": byProduct,
        "By Day Of Week": byDayOfWeek,
        "By Venue": byVenue,
        "By City": byCity,
        "By Active Days": byActiveDays,
        Orders: orders
      },
      SheetNames: [
        "Period Totals",
        "By Product",
        "By Day Of Week",
        "By Venue",
        "By City",
        "By Active Days",
        "Orders"
      ]
    };
    const excelBuffer: any = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });
    this.saveAsExcelFile(
      excelBuffer,
      "analytics_" +
        moment()
          .format("ll")
          .replace(", ", "_")
          .replace(" ", "_")
    );
  }
  private saveAsExcelFile(buffer: any, fileName: string): void {
    const data: Blob = new Blob([buffer], { type: this.EXCEL_TYPE });
    FileSaver.saveAs(data, fileName + this.EXCEL_EXTENSION);
  }

  private api: OrderApi;
  public subscription: Subscription;

  public byProductType: ByProductType[];
  public byDayOfWeek: ByDayOfWeek[];
  public byCity: ByCity[];
  public byVenue: ByVenue[];
  public byActiveDays: ByActiveDays[];

  public activeDaysSetting: ActiveDaysSetting = ActiveDaysSetting.DatePurchased;

  public periodTotals: Totals;

  public barChartData: BarChartData;

  private date: DateService = new DateService();
  public filter: AnalyticsFilterService;
  public orders$: Observable<Order[]>;
  public orders: Order[];

  public venues: Venue[];

  public waiting: boolean = true;

  constructor(public db: AngularFirestore, public venue?: Venue) {
    this.initializeLocalVariables();
    this.observe();
    this.subscribe();
  }
  initializeAccessibleVenues(venues: Venue[]) {
    this.venues = venues;
  }
  initializeLocalVariables() {
    this.api = new OrderApi(
      "devOrders",
      this.db,
      this.venue ? this.venue.name : ""
    );
    this.date = new DateService();
    this.orders = [];
  }
  observe = (tf?: Timeframe) => {
    this.orders$ = this.api.getAll(tf ? tf : this.date.monthToDate);
  };

  subscribe(changedTimeframe?: boolean) {
    this.subscription = this.orders$.subscribe(async orders => {
      this.orders = orders;

      if (this.filter == null) {
        this.filter = new AnalyticsFilterService();
        this.filter.init(orders);
        if (this.venue) {
          this.filter.currentVenue = this.venue;
          this.filter.venues = this.venues.map(v => v.name);
          this.filter.filter();
        }
        this.updateMetrics();
      } else this.update(orders);
      if (changedTimeframe) {
        this.filter.filtered = orders;
        this.filter.filter();
        this.updateMetrics();
      }
      if (this.waiting) {
        if (
          this.filter.currentTimeframe == "All Time" ||
          this.filter.currentTimeframe == "Year to Date" ||
          this.filter.currentTimeframe == "Last 12 Months"
        )
          setTimeout(() => (this.waiting = false), 8000);
        else setTimeout(() => (this.waiting = false), 3000);
      }
    });
  }

  get filtered(): Order[] {
    return this.filter.filtered;
  }

  reset() {
    this.filter.filtered = [];
    this.waiting = true;
    this.byActiveDays = [];
    this.byCity = [];
    this.byVenue = [];
    this.byProductType = [];
    this.periodTotals = new Totals();
    this.barChartData = null;
    this.updateMetrics();
  }
  changeTimeframe(key: string, timeframe: Timeframe) {
    this.filter.currentTimeframe = key;
    this.reset();
    this.getOrders(true, timeframe);
  }

  async getOrders(changedTimeframe?: boolean, timeframe?: Timeframe) {
    this.subscription.unsubscribe();
    this.observe(timeframe);
    this.subscribe(changedTimeframe);
  }

  /** BEGIN LISTENER FUNCTIONS */

  update(orders: Order[]) {
    this.filter.update(orders);
    this.updateMetrics();
  }

  updateMetrics() {
    this._prepareBarChart();
    this._preparePeriodTotals();
    this._prepareByProduct();
    this._prepareByDaysOfWeek();
    this._prepareByActiveDays();
    this._prepareByCity();
    this._prepareByVenue();
  }

  get revenue(): number {
    return this.periodTotals.revenue;
  }

  get unitsSold(): number {
    return this.periodTotals.units_sold;
  }

  get uniqueCustomers(): number {
    return this.periodTotals.unique_customers;
  }
  get newCustomers(): number {
    return this.periodTotals.new_customers;
  }
  get activeVenues(): number {
    return this.periodTotals.active_venues;
  }

  _prepareBarChart(): void {
    this.barChartData = { data: [], labels: [], dates: [] };
    var dates = this._getUniqueDates();
    dates.forEach(date => {
      this.barChartData.data.push(
        this._filterDate(date).reduce(
          (acc, order) => acc + order.summary.total,
          0
        )
      );
      this.barChartData.labels.push(
        date.getMonth() + 1 + "/" + date.getDate() + "/" + date.getFullYear()
      );
      this.barChartData.dates.push(date);
    });
  }

  /**
   * Function: _preparePeriodTotals
   * Description: Calculates Revenue, Unique Users, Units Sold
   */
  async _preparePeriodTotals(): Promise<void> {
    this.periodTotals = this.filtered.reduce((totals, order) => {
      totals.units_sold += order.items[0].summary.units;
      totals.revenue += order.summary.total;
      return totals;
    }, new Totals());
    this.periodTotals.unique_customers = this.getUniqueCustomers().length;
    this.periodTotals.new_customers = 0;
  }

  /**
   * Function: getUniqueCustomers
   * Description: Counts Unique Users in Order List
   */
  getUniqueCustomers = (): DocumentReference[] => [
    ...new Set(this.filtered.map(order => order.user.reference))
  ];

  /**
   * Function: prepareByProduct
   * Description: Categorizes orders by Product Type.
   */
  _prepareByProduct(): void {
    this.byProductType = [];
    Object.keys(this.filter.productTypes).forEach(type => {
      this.byProductType.push(
        new ByProductType(
          this._filterProductType(type),
          type,
          this.periodTotals.revenue
        )
      );
    });
  }

  /**
   * Function: prepareByDaysOfWeek
   * Description: Categorizes orders by Day Of Week for the Calendar Event
   */
  _prepareByDaysOfWeek(): void {
    this.byDayOfWeek = [];
    [0, 1, 2, 3, 4, 5, 6].forEach(day => {
      this.byDayOfWeek.push(
        new ByDayOfWeek(
          this._filterDayOfWeek(day),
          day,
          this.periodTotals.revenue
        )
      );
    });
  }

  /**
   * Function: prepareByActiveDays
   * Description: Categorizes orders by Unique Dates
   * Options: PurchaseDate | RedemptionDate | EventDate
   */
  _prepareByActiveDays() {
    this.byActiveDays = [];
    var dates = this._getUniqueDates();
    dates.forEach(date =>
      this.byActiveDays.push(
        new ByActiveDays(
          this._filterDate(date),
          date,
          this.periodTotals.revenue
        )
      )
    );
  }

  _prepareByCity() {
    this.byCity = [];
    var cities: string[] = this._getUniqueCities();
    var entries: ByCity[] = [];
    var nextFive: ByCity[] = [];
    var remaining: ByCity[] = [];
    cities.forEach(city => {
      var o: Order[] = this._filterCity(city);
      entries.push(new ByCity(o, this.periodTotals.revenue, city));
    });
    entries.sort((a, b) => b.total - a.total);
    for (var i = 0; i < entries.length; i++) {
      if (i < 10) this.byCity.push(entries[i]);
      if (i >= 10 && i < 15) nextFive.push(entries[i]);
      if (i >= 15) remaining.push(entries[i]);
    }

    var fOrders: Order[] = nextFive.reduce((acc, bycity) => {
      acc.push(...bycity.orders);
      return acc;
    }, []);

    var rOrders: Order[] = remaining.reduce((acc, bycity) => {
      acc.push(...bycity.orders);
      return acc;
    }, []);
    if (fOrders.length != 0)
      this.byCity.push(
        new ByCity(fOrders, this.periodTotals.revenue, "Next 5 Cities")
      );
    if (rOrders.length != 0)
      this.byCity.push(
        new ByCity(rOrders, this.periodTotals.revenue, "Remaining Cities")
      );
  }
  _filterCity(id: string) {
    return this.filtered.filter(
      order => order.city.name + ", " + order.city.state == id
    );
  }
  _getUniqueCities(): string[] {
    return this.filtered.reduce((acc, order) => {
      if (!acc.includes(order.city.name + ", " + order.city.state))
        acc.push(order.city.name + ", " + order.city.state);
      return acc;
    }, []);
  }
  _prepareByVenue() {
    this.byVenue = [];
    var venues: string[] = this._getUniqueVenues();
    this.periodTotals.active_venues = venues.length;
    var entries: ByVenue[] = []; //1-10
    var next10: ByVenue[] = []; //11-30
    var remaining: ByVenue[] = []; //31+
    venues.forEach(venue => {
      var o: Order[] = this._filterVenue(venue);
      if (o.length != 0)
        entries.push(
          new ByVenue(
            o,
            this.periodTotals.revenue,
            o[0].venue.name,
            o[0].city.name
          )
        );
    });
    entries.sort((a, b) => b.total - a.total);
    for (var i = 0; i < entries.length; i++) {
      if (i < 10) this.byVenue.push(entries[i]);
      if (i >= 10 && i < 20) next10.push(entries[i]);
      if (i >= 21) remaining.push(entries[i]);
    }
    var orders: Order[] = next10.reduce((acc, byVenue) => {
      acc.push(...byVenue.orders);
      return acc;
    }, []);
    if (orders.length != 0)
      this.byVenue.push(
        new ByVenue(orders, this.periodTotals.revenue, "Next 10 Venues", "")
      );
    orders = remaining.reduce((acc, byVenue) => {
      acc.push(...byVenue.orders);
      return acc;
    }, []);

    if (orders.length != 0)
      this.byVenue.push(
        new ByVenue(orders, this.periodTotals.revenue, "Remaining Venues", "")
      );
  }
  _filterVenue(ref: string) {
    return this.filtered.filter(order => order.venue.reference.path == ref);
  }

  _getUniqueVenues(): string[] {
    return this.filtered.reduce((acc, order) => {
      if (!acc.includes(order.venue.reference.path))
        acc.push(order.venue.reference.path);
      return acc;
    }, []);
  }

  /**
   * Function: filterProductType
   * Description: Returns subset of orders with corresponding type
   * @param type - Product Type
   */
  _filterProductType = (type: string): Order[] =>
    this.filtered.filter(
      order => ProductType[order.items[0].product.type] === type
    );

  /**
   * Function: filterDayOfWeek
   * Description: Returns subset of orders with calendar event on given day of week
   * @param day - Day Of Week [0,1,2,3,4,5,6]
   */
  _filterDayOfWeek = (day: number): Order[] =>
    this.filtered.filter(
      order => order.items[0].event.date.toDate().getDay() == day
    );

  /**
   * Function: filterDate
   * Description: Returns subset of orders with same date reference as [date]
   * @param date: Date Filter
   */
  _filterDate = (date: Date): Order[] =>
    this.filtered.filter(order => this._isSameDate(date, this._getDate(order)));

  /**
   * Function: getUniqueDates
   * Description: Gets List of unique dates from orders
   */
  _getUniqueDates = (): Date[] =>
    this._unique(
      this.filtered
        .reduce((arg: moment.Moment[], order: Order) => {
          var d = this._getDate(order);
          if (d != null) arg.push(moment(d));
          return arg;
        }, [])
        .reverse()
    );

  /**
   * Function: getDate
   * Description: Grabs the correct date reference depending on UI Setting for Table
   * @param order - Order to get date from
   */
  _getDate(order: Order): Date {
    switch (this.activeDaysSetting) {
      case ActiveDaysSetting.DateOfEvent:
        return order.items[0].event.date.toDate();
      case ActiveDaysSetting.DatePurchased:
        return order.summary.purchasedAt.toDate();
      case ActiveDaysSetting.DateRedeemed:
        return order.items[0].redeemedAt == null
          ? null
          : order.items[0].redeemedAt;
    }
  }

  /**
   * Function: unique
   * Description: Given a list of [dates], unique returns a list of unique dates
   * @param dates - list of order dates with duplicates
   */
  _unique = (dates: moment.Moment[]): Date[] => this.date.getUniqueDates(dates);

  /**
   * Function: isSameDate
   * Description: Returns if Date [a] and Date [b] are the same calendar date
   * @param a - Date to Compare
   * @param b - Date to Compare
   */
  _isSameDate = (a: Date, b: Date): boolean => this.date.isSameDate(a, b);
}

export enum ActiveDaysSetting {
  DateOfEvent,
  DateRedeemed,
  DatePurchased
}
